import { InvitationDescriptions } from "features/admin/group/InvitationModal/styles";
import Descriptions from "antd/es/descriptions";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import React from "react";
import CopyLink from "common/CopyToClipboard/CopyLink";
import { typography } from "@shared/common/Themes/typography";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { joinWords } from "@shared/common/i18n/utils";
import { LanguageNameSpaces } from "@shared/types";
import styled, { useTheme } from "styled-components";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

const InvitationSummary = ({ invitation, created }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { isNewLook } = useTheme();

  // New look: a smaller check, the result as a 16/600 line, the details as
  // label/value rows in a well, then the link to share.
  if (isNewLook)
    return (
      <NewLookWrapper>
        <AnimatedCheckIcon size={56} />
        <NewLookTitle>
          {t("invitations.summary.invitation")}{" "}
          {created
            ? t("invitations.summary.created")
            : t("invitations.summary.updated")}{" "}
          {t("invitations.summary.successfully")}
        </NewLookTitle>
        {invitation && (
          <>
            <NewLookDetails invitation={invitation} />
            <NewLookCopy>
              <NewLookCopyText>
                {t("invitations.summary.copyLinkMessage")}
              </NewLookCopyText>
              <CopyLink
                url={invitation.invitation_link}
                linkWidth={260}
                linkName={t("invitations.summary.inviteLink")}
              />
            </NewLookCopy>
          </>
        )}
      </NewLookWrapper>
    );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexFlow: "column",
      }}
    >
      <AnimatedCheckIcon size={100} />
      <Title level={2} style={{ margin: "12px 0 8px 0" }}>
        {joinWords([
          t("invitations.summary.invitation"),
          created
            ? t("invitations.summary.created")
            : t("invitations.summary.updated"),
          t("invitations.summary.successfully"),
        ])}
      </Title>

      {invitation && (
        <>
          <InvitationDetails invitation={invitation} />
          <Text style={{ marginTop: "32px" }}>
            {t("invitations.summary.copyLinkMessage")}
          </Text>
          <CopyLink
            url={invitation.invitation_link}
            linkWidth={200}
            linkName={t("invitations.summary.inviteLink")}
          />
        </>
      )}
    </div>
  );
};

export default InvitationSummary;

const NewLookDetails = ({ invitation }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const rows = [
    {
      key: "numUsers",
      label: t("invitations.summary.numUsers"),
      value: invitation.num_users
        ? `${invitation.num_users} ${
            invitation.num_users === 1
              ? t("invitations.summary.user")
              : t("invitations.summary.users")
          }`
        : t("invitations.summary.unlimited"),
    },
    invitation.username && {
      key: "username",
      label: t("invitations.summary.telegramUsername"),
      value: invitation.username,
    },
    invitation.telegram_user_id && {
      key: "telegramId",
      label: t("invitations.summary.telegramId"),
      value: invitation.telegram_user_id,
    },
    {
      key: "duration",
      label: t("invitations.summary.subscriptionDuration"),
      value: invitation.subscription_duration
        ? `${invitation.subscription_duration} ${
            invitation.subscription_duration === 1
              ? t("invitations.summary.day")
              : t("invitations.summary.days")
          }`
        : t("invitations.summary.lifetime"),
    },
  ].filter(Boolean);
  return (
    <DetailsWell>
      {rows.map((row) => (
        <DetailRow key={row.key}>
          <DetailLabel>{row.label}</DetailLabel>
          <DetailValue>{row.value}</DetailValue>
        </DetailRow>
      ))}
    </DetailsWell>
  );
};

const InvitationDetails = ({ invitation }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const labelWeight = typography.fontWeights.MEDIUM;
  return (
    <InvitationDescriptions
      title=""
      bordered
      style={{ marginTop: "14px" }}
      column={1}
    >
      <Descriptions.Item
        label={
          <Text $weight={labelWeight}>{t("invitations.summary.numUsers")}</Text>
        }
      >
        <Text>
          {invitation.num_users || t("invitations.summary.unlimited")}
          {!!invitation.num_users &&
            ` ${
              invitation.num_users === 1
                ? t("invitations.summary.user")
                : t("invitations.summary.users")
            }`}
        </Text>
      </Descriptions.Item>
      {invitation.username && (
        <Descriptions.Item
          label={
            <Text $weight={labelWeight}>
              {t("invitations.summary.telegramUsername")}
            </Text>
          }
        >
          <Text>{invitation.username}</Text>
        </Descriptions.Item>
      )}
      {invitation.telegram_user_id && (
        <Descriptions.Item
          label={
            <Text $weight={labelWeight}>
              {t("invitations.summary.telegramId")}
            </Text>
          }
        >
          <Text>{invitation.telegram_user_id}</Text>
        </Descriptions.Item>
      )}
      <Descriptions.Item
        label={
          <Text $weight={labelWeight}>
            {t("invitations.summary.subscriptionDuration")}
          </Text>
        }
      >
        <Text>
          {invitation.subscription_duration ||
            t("invitations.summary.lifetime")}
          {!!invitation.subscription_duration &&
            ` ${
              invitation.subscription_duration === 1
                ? t("invitations.summary.day")
                : t("invitations.summary.days")
            }`}
        </Text>
      </Descriptions.Item>
    </InvitationDescriptions>
  );
};

const NewLookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const NewLookTitle = styled.div`
  margin: 8px 0 16px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: center;
`;
const DetailsWell = styled.div`
  width: 100%;
  padding: 4px 16px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const DetailLabel = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const DetailValue = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: right;
`;
const NewLookCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 20px;
`;
const NewLookCopyText = styled.div`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
